namespace $ {

	export type $shm_hitalama_map_component_compiler_source = {
		fqn: string
		tree: string
		logic: string
		style: string
		format: 'ts' | 'js'
	}

	export type $shm_hitalama_map_component_compiler_output = {
		signature: string
		tree: string
		logic: string
		style: string
		locale: Record< string, string >
		bindings: { tree: string[], logic: string[], style: string[] }
		references: string[]
	}

	/** Pure compilation artifacts, independent of a map instance or its data. */
	export class $shm_hitalama_map_component_compiler extends $mol_object {

		static abi = 'tree-ts-3'
		compilations = 0
		cache_hits = 0

		@ $mol_mem
		cache(): $shm_hitalama_map_component_compiler_cache { return this.$.$shm_hitalama_map_component_compiler_cache.shared() }

		@ $mol_mem
		typescript() {
			const ts = $mol_import.script( 'shm/hitalama/lib/ts/typescript.js' ).ts
			if( ts.version !== this.$.$shm_hitalama_map_component_compiler_version.version ) {
				throw new Error( 'Версия TypeScript изменилась. Обновите страницу.' )
			}
			return ts
		}

		signature( source: $shm_hitalama_map_component_compiler_source ) {
			return JSON.stringify({ abi: $shm_hitalama_map_component_compiler.abi,
				ts: this.$.$shm_hitalama_map_component_compiler_version,
				options: $shm_hitalama_map_runtime_ts.compilerOptions, source })
		}

		@ $mol_mem_key
		compiled( signature: string ): $shm_hitalama_map_component_compiler_output {
			const cache = this.cache()
			const key = String( $mol_hash_string( signature ) ) + ':' + signature.length
			const cached = this.$.$mol_error_fence( ()=> $mol_wire_sync( cache ).read( key ), ()=> null )
			if( this.cache_valid( cached, signature ) ) {
				++ this.cache_hits
				return cached!
			}
			// Stabilize generation before the asynchronous write can suspend/replay.
			const output = this.generate( signature )
			this.$.$mol_error_fence( ()=> $mol_wire_sync( cache ).write( key, output ), ()=> false )
			return output
		}

		cache_valid( cached: $shm_hitalama_map_component_compiler_output | null, signature: string ) {
			if( cached?.signature !== signature || !cached.bindings || !cached.locale ) return false
			for( const kind of [ 'tree', 'logic', 'style' ] as const ) {
				if( typeof cached[ kind ] !== 'string' || !Array.isArray( cached.bindings[ kind ] ) ) return false
				if( !cached.bindings[ kind ].every( name => typeof name === 'string' && /^\$[A-Za-z0-9_]+$/.test( name ) ) ) return false
			}
			return Array.isArray( cached.references ) && cached.references.every( name => typeof name === 'string' )
		}

		/** One native IDB transaction for the complete dependency graph. */
		@ $mol_mem_key
		cache_keys( signatures: readonly string[] ) {
			return signatures.map( signature => String( $mol_hash_string( signature ) ) + ':' + signature.length )
		}

		@ $mol_mem_key
		prepared_many( signatures: readonly string[] ) {
			const keys = this.cache_keys( signatures )
			const cached = this.$.$mol_error_fence(
				()=> $mol_wire_sync( this.cache() ).read_many( keys ), ()=> signatures.map( ()=> null ),
			)
			const missing: [ string, $shm_hitalama_map_component_compiler_output ][] = []
			let hits = 0
			const outputs = signatures.map( ( signature, index )=> {
				if( this.cache_valid( cached[index], signature ) ) { ++ hits; return cached[index]! }
				const output = this.generate( signature )
				missing.push([ keys[index], output ])
				return output
			})
			return { outputs, missing, hits }
		}

		@ $mol_mem_key
		compiled_many( signatures: readonly string[] ) {
			const plan = this.prepared_many( signatures )
			if( plan.missing.length ) this.$.$mol_error_fence(
				()=> $mol_wire_sync( this.cache() ).write_many( plan.missing ), ()=> false,
			)
			this.cache_hits += plan.hits
			return plan.outputs
		}

		@ $mol_mem_key
		generate( signature: string ): $shm_hitalama_map_component_compiler_output {
			const source = JSON.parse( signature ).source as $shm_hitalama_map_component_compiler_source
			const parsed = this.$.$mol_tree2_from_string( source.tree, source.fqn + '.view.tree' )
			const tree = parsed.kids.length ? this.$.$mol_view_tree2_normalize( parsed ) : null
			if( tree && ( tree.kids.length !== 1 || tree.kids[0].type !== source.fqn ) ) {
				throw new Error( `view.tree должен определять один компонент ${ source.fqn }` )
			}
			const generated = tree ? this.$.$mol_tree2_text_to_string_mapped_js(
				this.$.$mol_tree2_js_to_text( this.$.$mol_view_tree2_to_js( tree ) ),
			) : ''
			const output = {
				signature,
				tree: String( generated ),
				logic: source.format === 'ts' ? this.transpile( source.logic, source.fqn + '.view.ts' ) : source.logic,
				style: source.format === 'ts' ? this.transpile( source.style, source.fqn + '.view.css.ts' ) : source.style,
				locale: tree ? this.$.$mol_view_tree2_to_locale( tree ) : {},
				bindings: { tree: [] as string[], logic: [] as string[], style: [] as string[] },
				references: [] as string[],
			}
			for( const kind of [ 'tree', 'logic', 'style' ] as const ) output.bindings[ kind ] = this.bindings( output[ kind ] )
			const references = new Set< string >
			const visit = ( node: $mol_tree2 )=> {
				if( node.type.startsWith( '$' ) ) references.add( node.type )
				for( const child of node.kids ) visit( child )
			}
			if( tree ) visit( tree )
			output.references = [ ...references ]
			++ this.compilations
			return output
		}

		/** Parse identifiers rather than guessing bindings from strings and comments. */
		bindings( code: string ) {
			const ts = this.typescript()
			const file = ts.createSourceFile( 'component.js', code, ts.ScriptTarget.Latest, true, ts.ScriptKind.JS )
			const names = new Set< string >
			const local = new Set< string >
			const binding = ( node: any )=> {
				if( ts.isIdentifier( node ) ) local.add( node.text )
				else for( const element of node.elements ?? [] ) if( element.name ) binding( element.name )
			}
			for( const statement of file.statements ) {
				if( ts.isVariableStatement( statement ) ) for( const declaration of statement.declarationList.declarations ) binding( declaration.name )
				else if( statement.name && ( ts.isFunctionDeclaration( statement ) || ts.isClassDeclaration( statement ) ) ) binding( statement.name )
				if( ts.isImportDeclaration( statement ) || ts.isExportDeclaration( statement ) ) {
					throw new Error( 'Используйте явные зависимости компонентов вместо import/export модулей.' )
				}
			}
			const visit = ( node: any )=> {
				if( ts.isIdentifier( node ) && /^\$[A-Za-z0-9_]+$/.test( node.text ) ) names.add( node.text )
				ts.forEachChild( node, visit )
			}
			visit( file )
			return [ ...names ].filter( name => !local.has( name ) )
		}

		transpile( source: string, fileName: string ) {
			const ts = this.typescript()
			const result = ts.transpileModule( source, {
				compilerOptions: { ...$shm_hitalama_map_runtime_ts.compilerOptions, sourceMap: false, inlineSourceMap: true, inlineSources: true },
				fileName,
				reportDiagnostics: true,
			})
			const errors = ( result.diagnostics ?? [] ).filter( ( item: any )=> item.category === ts.DiagnosticCategory.Error )
			if( errors.length ) throw new Error( errors.map( ( item: any )=> {
				const pos = item.file?.getLineAndCharacterOfPosition( item.start ?? 0 )
				return `${ fileName }:${ ( pos?.line ?? 0 ) + 1 }:${ ( pos?.character ?? 0 ) + 1 }: ${ ts.flattenDiagnosticMessageText( item.messageText, '\n' ) }`
			}).join( '\n' ) )
			return result.outputText
		}

	}

}
